import { addDays, format, formatDate, isSameDay, startOfToday } from "date-fns";
import { DaysOfWeek, TimeOfDay } from "~/entities/baseEntities";
import { toZonedTime } from "date-fns-tz";

export const formatReadyTime = (
  requestedReadyTime: string | null | undefined,
  timeZone: string,
  asapWait: number | null | undefined,
  lowerCase?: boolean
) =>
  requestedReadyTime
    ? `${formatDay(toZonedTime(requestedReadyTime, timeZone))}, ${formatTime(
        toZonedTime(requestedReadyTime, timeZone)
      )}`
    : asapWait
    ? lowerCase
      ? `Today, ASAP (about ${asapWait} minutes)`
      : `Today, ASAP (About ${asapWait} Minutes)`
    : "Today, ASAP";

/**
 * Returns as format e.g. "12/04/23".
 */
export const formatDateTime = (date: Date) => {
  if (isSameDay(startOfToday(), date)) {
    return "Today";
  }
  if (isSameDay(addDays(startOfToday(), 1), date)) {
    return "Tomorrow";
  }
  return format(date, "dd/MM/yy");
};

// format to dd.MM.yyyy
export const formatDateValue = (date: Date) => {
  return format(date, "dd.MM.yyyy");
};

/**
 * Returns as format e.g. "12th Apr".
 */
export const formatDay = (date: Date) => {
  if (isSameDay(startOfToday(), date)) {
    return "Today";
  }
  if (isSameDay(addDays(startOfToday(), 1), date)) {
    return "Tomorrow";
  }
  return formatDate(date, "do MMM");
};

/**
 * Returns as format e.g. "1:23 PM"
 * @param dateTime
 */
export const formatTime = (dateTime: Date): string => {
  return format(dateTime, "h:mm a");
};

/**
 * Returns as format e.g. "1:23 PM tomorrow"
 * @param dateTime
 */
export const formatTimeAndDay = (dateTime: Date): string => {
  const time = formatTime(dateTime);
  if (isSameDay(startOfToday(), dateTime)) {
    return time;
  }
  const day = formatDay(dateTime);
  return `${day} ${time}`;
};

export const timeFromObject = (value: TimeOfDay | null) => {
  if (!value) return null;

  const date = new Date().setHours(value.hour, value.minute, 0);

  return format(date, "h:mm aa");
};

export const displayTimeOfDay = (x: TimeOfDay) => {
  const minuteText = x.minute < 10 ? `0${x.minute}` : `${x.minute}`;
  return `${x.hour}:${minuteText}`;
};

export const displayTimeOfDayShort = (x: TimeOfDay, no24Hour?: boolean) => {
  let hourText: string;
  let suffix = "";

  if (no24Hour) {
    if (x.hour === 0 || x.hour === 24) {
      hourText = "12";
      suffix = "am";
    } else if (x.hour < 12) {
      hourText = `${x.hour}`;
      suffix = "am";
    } else if (x.hour === 12) {
      hourText = "12";
      suffix = "pm";
    } else {
      hourText = `${x.hour - 12}`;
      suffix = "pm";
    }
  } else {
    hourText = `${x.hour}`;
  }

  if (x.minute === 0) {
    return `${hourText}${suffix}`;
  }
  const minuteText = x.minute < 10 ? `0${x.minute}` : `${x.minute}`;

  return `${hourText}:${minuteText}${suffix}`;
};

export const combineDays = (applies: DaysOfWeek) => {
  const days = Object.values(applies).reduce(
    (res: number[], cur, index) => (cur ? [...res, index] : res),
    []
  );
  const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  days.sort((a, b) => a - b);
  const ranges = [];
  let lastDay = null;

  for (const day of days) {
    if (lastDay === null || day - lastDay !== 1) {
      ranges.push([day]);
    } else {
      ranges[ranges.length - 1].push(day);
    }
    lastDay = day;
  }

  let output = "";
  for (let i = 0; i < ranges.length; i++) {
    const range = ranges[i];
    if (range.length === 1) {
      output += daysOfWeek[range[0]];
    } else {
      output += `${daysOfWeek[range[0]]}-${
        daysOfWeek[range[range.length - 1]]
      }`;
    }
    if (i !== ranges.length - 1) {
      output += ", ";
    }
  }
  return output;
};

/**
 * Returns a date in the past relative to today.
 * @param yearsAgo Number of years in the past.
 */
export const getDateYearsAgo = (yearsAgo: number): string => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - yearsAgo);
  return date.toISOString().split("T")[0];
};

export const getWaitTime = (from: Date, to: Date): string => {
  const diffMs = Math.max(0, to.getTime() - from.getTime());
  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) return `(~${hours} h ${minutes} min wait)`;
  if (hours > 0) return `(~${hours} h wait)`;
  return `~(${minutes} min wait)`;
};
